import React, { useState, useEffect, use } from 'react';
import axios from 'axios'; // Assuming you might need axios for delete
import {
    Container,
    Grid,
    Card,
    CardContent,
    CardActions,
    CardHeader,
    Button,
    Typography,
    CircularProgress,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    CardMedia
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

// --- Helper function mocks (replace with your actual implementations) ---
import { deleteScormCourse, fetchAllScormCourse } from 'helpers/scorm_helper';
import { toast } from 'react-toastify';
import { LAUNCH_COURSE } from 'helpers/url_helper';
import { set } from 'lodash';
import { addScormCourseToBatch, removeScormCourseFromBatch } from 'helpers/batch_helper';
import { useParams } from 'react-router-dom';

const ScormCourse = ({ allScormCourses, allScormCoursesOfBatch, type, getScormCourseOfBatch }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // State for delete confirmation dialog
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [openAddCourseDialog, setOpenAddCourseDialog] = useState(false);
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [courseToDelete, setCourseToDelete] = useState(null);
    const params = useParams();
    // --- Handlers ---

    const studentId = "not required"; // replace with your actual logged-in student ID
    const userType = "admin"; // or set a default type

    const handleLaunchCourse = (course) => {
        // Using window.location.href causes a full page reload.
        // For a smoother SPA experience, consider using React Router's useHistory or useNavigate.
        let courseId = course.id;
        window.open(`${LAUNCH_COURSE}/${course.storagePath}?courseId=${courseId}&studentId=${studentId}&type=${userType}`, '_blank');
    };

    const handleDeleteClick = (course) => {
        setCourseToDelete(course);
        setOpenConfirmDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenConfirmDialog(false);
        setCourseToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!courseToDelete) return;
        setLoading(true);
        try {
            const data = await removeScormCourseFromBatch(params.id + "/" + courseToDelete.id);
            if (data?.success) {
                toast.success(data?.message);
                getScormCourseOfBatch();
            } else {
                toast.error(data?.message);
            }

        } catch (err) {
            console.error('Error deleting course:', err);
            setError(`Failed to delete ${courseToDelete.title}.`);
        } finally {
            // Close the dialog regardless of success or failure
            handleCloseDialog();
            setLoading(false);
        }
    };


    const handleAddCourseToBatch = async (id) => {
        try {
            setLoading(true);
            const data = await addScormCourseToBatch(params.id + "/" + id);

            if (data?.success) {
                toast.success(data?.message);
                getScormCourseOfBatch();
                setOpenAddCourseDialog(false);
                setSelectedCourseId(null);
            } else {
                setError(`Failed to delete ${courseToDelete.title}.`);
            }

        } catch (error) {
            setError(`Failed to delete ${courseToDelete.title}.`);
        } finally {
            setLoading(false);
        }
    }


    // --- Render Logic ---

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="90vh">
                <CircularProgress />
                <Typography variant="h6" ml={2}>Loading Courses...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Container>
                <Typography color="error" align="center" variant="h6">{error}</Typography>
            </Container>
        )
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <div className="d-flex justify-content-end mt-2">
                {type != "Trainer" &&
                    <Button
                        variant="outlined"
                        onClick={() => setOpenAddCourseDialog(true)}
                    >
                        Add Course
                    </Button>
                }
            </div>
            <Typography variant="h4" component="h1" gutterBottom>
                SCORM Courses
            </Typography>
            <Grid container spacing={4}>
                {allScormCoursesOfBatch.length > 0 ? (
                    allScormCoursesOfBatch.map(pkg => (
                        <Grid item key={pkg.id} xs={12} sm={6} md={4}>
                            <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                {pkg.thumbnailUrl ? (
                                    <CardMedia
                                        component="img"
                                        height="300"
                                        image={pkg.thumbnailUrl}
                                        alt={`${pkg.title} thumbnail`}
                                    />
                                ) : (
                                    <Box
                                        height="100px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            backgroundColor: '#f5f5f5',
                                            color: '#888',
                                            fontStyle: 'italic'
                                        }}
                                    >
                                        No Thumbnail
                                    </Box>
                                )}
                                <CardHeader
                                    title={pkg.title}
                                    // subheader={`Version: ${pkg.version || 'N/A'}`}
                                    action={
                                        <IconButton
                                            aria-label="delete course"
                                            onClick={() => handleDeleteClick(pkg)}
                                        >
                                            <DeleteIcon sx={{ color: 'red' }} />
                                        </IconButton>
                                    }
                                />
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {/* You can add a course description here if available */}
                                        {/* Click 'Launch' to start the course. */}
                                    </Typography>
                                </CardContent>
                                <CardActions>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<RocketLaunchIcon />}
                                        onClick={() => handleLaunchCourse(pkg)}
                                    >
                                        Start Course
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))
                ) : (
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" align="center">
                            No SCORM courses have been uploaded yet.
                        </Typography>
                    </Grid>
                )}
            </Grid>

            {/* Confirmation Dialog for Deletion */}
            <Dialog
                open={openConfirmDialog}
                onClose={handleCloseDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Confirm Deletion
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to remove the course
                        <strong>"{courseToDelete?.title}"</strong>? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained" autoFocus>
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openAddCourseDialog}
                onClose={() => setOpenAddCourseDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Add SCORM Course to Batch</DialogTitle>
                <DialogContent>
                    <DialogContentText>Select a course below to add it to this batch.</DialogContentText>
                    <Box mt={2}>
                        <Grid container spacing={2}>
                            {allScormCourses && allScormCourses.length > 0 ? (
                                allScormCourses
                                    .filter((course) => !allScormCoursesOfBatch.some((c) => c.id === course.id))
                                    .map((course) => (
                                        <Grid item xs={12} sm={6} md={4} key={course.id}>
                                            <Card
                                                variant={selectedCourseId === course.id ? "outlined" : undefined}
                                                sx={{
                                                    border: selectedCourseId === course.id ? '2px solid #1976d2' : '1px solid #eee'
                                                }}
                                                onClick={() => setSelectedCourseId(course.id)}
                                            >
                                                {course.thumbnailUrl ? (
                                                    <CardMedia
                                                        component="img"
                                                        height="180"
                                                        image={course.thumbnailUrl}
                                                        alt={`${course.title} thumbnail`}
                                                    />
                                                ) : (
                                                    <Box
                                                        height="180px"
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="center"
                                                        sx={{
                                                            backgroundColor: '#f5f5f5',
                                                            color: '#888',
                                                            fontStyle: 'italic'
                                                        }}
                                                    >
                                                        No Thumbnail
                                                    </Box>
                                                )}
                                                <CardHeader title={course.title} subheader={`Version: ${course.version || "N/A"}`} />
                                                <CardContent>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {course.description || 'No description'}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))
                            ) : (
                                <Grid item xs={12}>
                                    <Typography>No courses available for addition.</Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAddCourseDialog(false)} color="inherit">Cancel</Button>
                    <Button
                        onClick={() => {
                            if (selectedCourseId) {
                                handleAddCourseToBatch(selectedCourseId);
                            }
                        }}
                        color="primary"
                        variant="contained"
                        disabled={!selectedCourseId || loading}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Add Course'}
                    </Button>
                </DialogActions>
            </Dialog>

        </Container>
    );
};

export default ScormCourse;